import type {
  EvaluationData,
  GetDataRoomContentResponse,
  InvestorEvaluationRequestData,
} from '@workspace/api-backend'
import { Badge } from '@workspace/ui/components/badge'
import { Banner } from '@workspace/ui/components/banner'
import { Button } from '@workspace/ui/components/button'
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@workspace/ui/components/collapsible'
import { LabelValue } from '@workspace/ui/components/label-value'
import { MetricCard } from '@workspace/ui/components/metric-card'
import { PitchCard } from '@workspace/ui/components/pitch-card'
import { ProfileHeader } from '@workspace/ui/components/profile-header'
import { ProfileSection } from '@workspace/ui/components/profile-section'
import { Tabs, TabsList, TabsTrigger } from '@workspace/ui/components/tabs'
import { cn, formatCurrency, formatDate } from '@workspace/ui/lib/utils'
import {
  Calendar,
  ChevronDown,
  EyeOff,
  Layers,
  MapPin,
  Sparkles,
  Target,
  TrendingUp,
} from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import { MarkdownContent } from '@/components/markdown-content'
import { DocumentPreviewModal, type PreviewDocument } from '../../preview'
import { DocumentsList, stageConfig, TeamList } from '../../startup'
import { extractEvaluationData } from '../utils/scores'
import { MetricsTabs } from './metrics-tabs'
import { PitchAnalysisTabs } from './pitch-analysis-tabs'
import { VideoPreviewModal } from './video-preview-modal'
import { WinnerJudgeCard } from './winner-judge-card'

type DataRoomData = NonNullable<GetDataRoomContentResponse['data']> & {
  investorEvaluationRequest?: InvestorEvaluationRequestData | null
  customEvaluation?: EvaluationData | null
}

const EvaluationMode = {
  COUNCIL: 'council',
  CUSTOM: 'custom',
} as const
type EvaluationMode = (typeof EvaluationMode)[keyof typeof EvaluationMode]

interface ConfigOption {
  value: string
  label: string
}

export interface DataRoomContentProps {
  data: DataRoomData
  productStages: ConfigOption[]
  fetchVideoUrl?: () => Promise<string>
  fetchPitchDeckUrl?: () => Promise<string>
  fetchDocumentUrl: (documentId: string) => Promise<string>
  headerActions?: React.ReactNode
  footer?: React.ReactNode
  onVideoClose?: () => void
  onSectionChange?: (section: string, resourceId?: string) => void
  onRequestInvestorEvaluation?: () => void
  isRequestingEvaluation?: boolean
}

export function DataRoomContent({
  data,
  productStages,
  fetchVideoUrl,
  fetchPitchDeckUrl,
  fetchDocumentUrl,
  headerActions,
  footer,
  onVideoClose,
  onSectionChange,
  onRequestInvestorEvaluation,
  isRequestingEvaluation,
}: DataRoomContentProps) {
  const [videoUrl, setVideoUrl] = useState<string | null>(null)
  const [pitchDeckPreview, setPitchDeckPreview] = useState<PreviewDocument | null>(null)
  const [previewDocument, setPreviewDocument] = useState<PreviewDocument | null>(null)
  const [metricsOpen, setMetricsOpen] = useState(false)

  const { pitchSession, startup, members, documents, investorEvaluationRequest, customEvaluation } =
    data
  const hasCustomEvaluation = !!customEvaluation

  const [evaluationMode, setEvaluationMode] = useState<EvaluationMode>(
    hasCustomEvaluation ? EvaluationMode.CUSTOM : EvaluationMode.COUNCIL
  )

  // Auto-switch to custom mode when custom evaluation becomes available
  const prevHasCustom = useRef(hasCustomEvaluation)
  useEffect(() => {
    if (hasCustomEvaluation && !prevHasCustom.current) {
      setEvaluationMode(EvaluationMode.CUSTOM)
    }
    prevHasCustom.current = hasCustomEvaluation
  }, [hasCustomEvaluation])

  const councilEvaluation = pitchSession ? (pitchSession.evaluations?.[0] ?? null) : null
  const activeEvaluation =
    evaluationMode === EvaluationMode.CUSTOM && customEvaluation
      ? customEvaluation
      : councilEvaluation
  const evaluation = activeEvaluation
  const {
    individualEvaluations,
    collectiveMetricEvaluations,
    metricScores,
    totalScore,
    winnerJudge,
  } = extractEvaluationData(evaluation)

  const stageData = startup.productStage
    ? productStages.find((s) => s.value === startup.productStage)
    : null
  const stageColor = startup.productStage ? stageConfig[startup.productStage]?.color : undefined

  // Derive tracked section from modal state
  useEffect(() => {
    if (videoUrl) {
      onSectionChange?.('video')
    } else if (pitchDeckPreview) {
      onSectionChange?.('pitch_deck')
    } else if (previewDocument) {
      onSectionChange?.('document', previewDocument.id)
    } else {
      onSectionChange?.('page')
    }
  }, [videoUrl, pitchDeckPreview, previewDocument, onSectionChange])

  const handleVideoClick = async () => {
    if (!fetchVideoUrl) return
    const url = await fetchVideoUrl()
    setVideoUrl(url)
  }

  return (
    <>
      <ProfileHeader
        coverImageUrl={startup.coverImageUrl}
        avatarUrl={startup.logoUrl}
        name={startup.name}
        subtitle={startup.category?.name}
        badges={
          <span className="hidden md:contents">
            {stageData && (
              <Badge variant={stageColor} size="md">
                {stageData.label}
              </Badge>
            )}
            {startup.isStealth && (
              <Badge variant="secondary" size="md" className="gap-1">
                <EyeOff className="size-3" />
                Stealth
              </Badge>
            )}
            {startup.isFundraising && (
              <Badge variant="success" size="md" className="gap-1">
                <Sparkles className="size-3" />
                Raising
              </Badge>
            )}
          </span>
        }
        actions={headerActions}
        contentClassName="mx-auto max-w-6xl"
      />

      <div className="mx-auto w-full max-w-6xl space-y-6">
        <div className="flex flex-wrap items-center justify-between gap-6">
          <div className="flex flex-col gap-2 md:flex-row md:flex-wrap md:items-center md:gap-6">
            <LabelValue
              icon={<MapPin className="size-4" />}
              label="Location"
              value={startup.location}
              direction="horizontal"
            />
            <LabelValue
              icon={<Calendar className="size-4" />}
              label="Founded"
              value={formatDate(startup.foundingDate)}
              direction="horizontal"
            />
          </div>
          {startup.tags.length > 0 && (
            <div className="flex flex-wrap gap-2 md:ml-auto">
              {startup.tags.map((tag) => (
                <Badge key={tag} variant="secondary" size="md">
                  {tag}
                </Badge>
              ))}
            </div>
          )}
        </div>

        {startup.description && (
          <ProfileSection>
            <p className="text-white leading-relaxed">{startup.description}</p>
          </ProfileSection>
        )}

        {startup.isFundraising && (
          <Banner
            variant="success"
            icon={<Sparkles className="size-5" />}
            title="Actively Raising"
            description="This startup is currently seeking investment"
            actions={
              (startup.fundraisingAmount || startup.fundraisingStage) && (
                <div className="flex items-center gap-4 text-right">
                  {startup.fundraisingStage && (
                    <div>
                      <p className="text-xs text-muted-foreground">Round</p>
                      <p className="text-lg font-bold text-emerald-400">
                        {startup.fundraisingStage}
                      </p>
                    </div>
                  )}
                  {startup.fundraisingAmount && (
                    <div>
                      <p className="text-xs text-muted-foreground">Target</p>
                      <p className="text-lg font-bold text-white">
                        {formatCurrency(startup.fundraisingAmount)}
                      </p>
                    </div>
                  )}
                </div>
              )
            }
          />
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          <MetricCard
            variant="highlight"
            icon={<Target className="size-5" />}
            label={startup.northStarMetricName ?? 'North Star Metric'}
            value={startup.northStarMetricValue ?? '—'}
          />
          <MetricCard
            icon={<TrendingUp className="size-5" />}
            label="ARR"
            value={formatCurrency(startup.arr)}
          />
          <MetricCard
            icon={<Layers className="size-5" />}
            label="Product Stage"
            value={stageData?.label ?? '—'}
          />
        </div>

        {pitchSession && (
          <>
            <ProfileSection title="Pitch Recording">
              <PitchCard
                pitchId={pitchSession.id}
                totalScore={totalScore}
                videoCoverImageUrl={pitchSession.videoCoverImageUrl}
                finishedAt={pitchSession.finishedAt ?? pitchSession.createdAt}
                judgeScores={metricScores}
                winnerJudge={winnerJudge}
                scoreLabel={
                  evaluationMode === EvaluationMode.CUSTOM && evaluation?.personaName ? (
                    <>
                      After evaluating all relevant data, this pitch was scored through{' '}
                      <span className="font-semibold text-white">{evaluation.personaName}'s</span>{' '}
                      investment thesis.
                    </>
                  ) : undefined
                }
                pitchDeck={
                  pitchSession.pitchDeck
                    ? {
                        id: pitchSession.pitchDeck.id,
                        fileName: pitchSession.pitchDeck.fileName,
                        label: 'Pitch Deck',
                      }
                    : null
                }
                onVideoClick={pitchSession.videoKey && fetchVideoUrl ? handleVideoClick : undefined}
                onPitchDeckClick={
                  fetchPitchDeckUrl && pitchSession.pitchDeck
                    ? (deck) => {
                        setPitchDeckPreview({
                          id: deck.id,
                          fileName: deck.fileName,
                          mimeType: 'application/pdf',
                        })
                      }
                    : undefined
                }
                footerActions={
                  onRequestInvestorEvaluation ? (
                    hasCustomEvaluation ? (
                      <Tabs
                        value={evaluationMode}
                        onValueChange={(v) => setEvaluationMode(v as EvaluationMode)}
                        className="w-full"
                      >
                        <TabsList className="w-full">
                          <TabsTrigger value={EvaluationMode.COUNCIL} className="flex-1">
                            Council Diligence
                          </TabsTrigger>
                          <TabsTrigger value={EvaluationMode.CUSTOM} className="flex-1">
                            Personalized Diligence
                          </TabsTrigger>
                        </TabsList>
                      </Tabs>
                    ) : investorEvaluationRequest || isRequestingEvaluation ? (
                      <div className="space-y-2">
                        <Button variant="outline" size="sm" className="w-full" disabled>
                          <Sparkles className="size-4" />
                          {investorEvaluationRequest?.status === 'PENDING_PERSONA'
                            ? 'Personalized profile processing...'
                            : 'Personalized diligence processing...'}
                        </Button>
                        <p className="text-xs text-center text-muted-foreground">
                          This may take up to 10 minutes
                        </p>
                      </div>
                    ) : (
                      <Button size="sm" className="w-full" onClick={onRequestInvestorEvaluation}>
                        <Sparkles className="size-4" />
                        Get Personalized Diligence
                      </Button>
                    )
                  ) : undefined
                }
              />
              {evaluation?.summary && (
                <div className="mt-4 rounded-lg border border-border bg-card p-4">
                  <MarkdownContent>{evaluation.summary}</MarkdownContent>
                </div>
              )}
            </ProfileSection>

            {evaluation && <PitchAnalysisTabs evaluation={evaluation} />}

            {collectiveMetricEvaluations.length > 0 && (
              <Collapsible open={metricsOpen} onOpenChange={setMetricsOpen}>
                <div className="rounded-lg border border-border bg-card overflow-hidden">
                  <CollapsibleTrigger className="flex w-full cursor-pointer items-center justify-between p-4 hover:bg-muted/50 transition-colors">
                    <span className="text-sm font-medium text-muted-foreground uppercase tracking-wider">
                      Metrics
                    </span>
                    <ChevronDown
                      className={cn(
                        'size-4 text-muted-foreground transition-transform',
                        metricsOpen && 'rotate-180'
                      )}
                    />
                  </CollapsibleTrigger>
                  <CollapsibleContent className="border-t border-border p-4">
                    <MetricsTabs metrics={collectiveMetricEvaluations} />
                  </CollapsibleContent>
                </div>
              </Collapsible>
            )}

            {winnerJudge && individualEvaluations.length > 0 && (
              <WinnerJudgeCard
                winnerPersona={winnerJudge.persona}
                evaluations={individualEvaluations}
                score={totalScore}
              />
            )}
          </>
        )}

        <ProfileSection title="Documents">
          <DocumentsList
            documents={documents}
            onPreview={(doc) => {
              setPreviewDocument({
                id: doc.documentId,
                fileName: doc.document.fileName,
                mimeType: doc.document.mimeType,
              })
            }}
          />
        </ProfileSection>

        <ProfileSection title="Team">
          <TeamList members={members} />
        </ProfileSection>

        {footer}
      </div>

      {videoUrl && (
        <VideoPreviewModal
          videoUrl={videoUrl}
          title={`${startup.name} - Pitch Recording`}
          open
          onOpenChange={(open) => {
            if (!open) {
              setVideoUrl(null)
              onVideoClose?.()
            }
          }}
        />
      )}

      {pitchDeckPreview && fetchPitchDeckUrl && (
        <DocumentPreviewModal
          document={pitchDeckPreview}
          open
          onOpenChange={(open) => {
            if (!open) {
              setPitchDeckPreview(null)
            }
          }}
          fetchUrl={fetchPitchDeckUrl}
        />
      )}

      {previewDocument && (
        <DocumentPreviewModal
          document={previewDocument}
          open
          onOpenChange={(open) => {
            if (!open) {
              setPreviewDocument(null)
            }
          }}
          fetchUrl={fetchDocumentUrl}
        />
      )}
    </>
  )
}
